import * as React from "react"
import { AudioButton, ContributorData, ControlsWrapper, PaginationButton, PaginationWrapper, PlayerContentWrapper, Recording, RecordingsList } from "./styles"
import { useLocale } from '../../providers'
import { Waveform } from "../Waveform"
import { playbackReducer, initialPlaybackState } from './playbackReducer'


const getWaveformIndex = (duration: number) => {
    if (duration < 30) return 0
    if (duration < 60) return 1
    if (duration < 90) return 2
    if (duration < 120) return 3
    if (duration < 150) return 4
    if (duration < 181) return 5
    return 1
}

type RecordingType = {
    _id: string
    _createdAt: string
    audio: { asset: { url: string } }
    duration: number
    contributorName?: string
    contributorLocation?: string
}

const MemoizedWaveform = React.memo(Waveform)

export const Player: React.FC = () => {
    const { getText } = useLocale()
    const [hasPlaybackError, setHasPlaybackError] = React.useState(false)
    const [recordings, setRecordings] = React.useState<RecordingType[]>([])
    const [loading, setLoading] = React.useState(true)
    const [fetchError, setFetchError] = React.useState(false)
    const [playbackState, dispatch] = React.useReducer(playbackReducer, initialPlaybackState)
    const [nextCursor, setNextCursor] = React.useState<string | null>(null)
    const [prevCursors, setPrevCursors] = React.useState<string[]>([])
    const [currentCursor, setCurrentCursor] = React.useState<string | null>(null)
    const audioRefs = React.useRef<Record<string, HTMLAudioElement | null>>({})
    const rafId = React.useRef<number | null>(null)
    const [waveformIndices, setWaveformIndices] = React.useState<Record<string, number>>({})


    const fetchRecordings = async (cursor: string | null) => {
        setLoading(true)
        setFetchError(false)
        try {
            const queryParam = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
            const res = await fetch(`/api/getRecordings${queryParam}`)
            const data = await res.json()
            setRecordings(data.recordings)
            const waveformIndicesFromDurations: Record<string, number> = {}

            data.recordings.forEach(rec => {
                waveformIndicesFromDurations[rec._id] = getWaveformIndex(rec.duration)
                dispatch({ type: 'SET_DURATION', id: rec._id, duration: rec.duration })
                dispatch({ type: 'RESET_PROGRESS', id: rec._id })
            })
            setNextCursor(data.nextCursor || null)
            setWaveformIndices(waveformIndicesFromDurations)
        } catch {
            setFetchError(true)
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        const loadInitial = async () => {
            await fetchRecordings(null)
        }
        loadInitial()
    }, [])

    const startProgressLoop = (id: string, audio: HTMLAudioElement) => {
        let progress = 0
        const duration = durationIsReal(audio.duration) ? audio.duration : playbackState[id]?.duration

        if (durationIsReal(duration)) {
            const loop = () => {
                const currentTime = audio.currentTime
                progress = (currentTime / duration) * 100
                dispatch({ type: 'SET_PROGRESS', id, progress })
                rafId.current = requestAnimationFrame(loop)
            }
            rafId.current = requestAnimationFrame(loop)
        }
    }

    const stopProgressLoop = () => {
        if (rafId.current) {
            cancelAnimationFrame(rafId.current)
            rafId.current = null
        }
    }

    React.useEffect(() => {
        return () => {
            stopProgressLoop()
        }
    }, [])

    const togglePlay = (id: string) => {
        const audio = audioRefs.current[id]
        if (!audio) return
        const isPlaying = playbackState[id]?.isPlaying
        if (isPlaying) {
            audio.pause()
            stopProgressLoop()
            dispatch({ type: 'PAUSE', id })
        } else {
            Object.entries(audioRefs.current).forEach(([key, aud]) => {
                if (key !== id && aud && !aud.paused) {
                    aud.pause()
                    dispatch({ type: 'PAUSE', id: key })
                }
            })
            audio.play()
            dispatch({ type: 'PLAY', id })
            startProgressLoop(id, audio)
        }
    }

    const goNext = () => {
        if (!nextCursor) return
        setPrevCursors(prev => [...prev, currentCursor || ''])
        setCurrentCursor(nextCursor)
        fetchRecordings(nextCursor)
    }

    const goPrev = () => {
        if (prevCursors.length === 0) return
        const newPrevCursors = [...prevCursors]
        const prevCursor = newPrevCursors.pop() || null
        setPrevCursors(newPrevCursors)
        setCurrentCursor(prevCursor)
        fetchRecordings(prevCursor)
    }

    const durationIsReal = (duration) =>
        !!duration && isFinite(duration) && duration > 0


    const handleAudioError = () => {
        setHasPlaybackError(true)
    }

    const formatShortDate = (dateString: string) => {
        const d = new Date(dateString);

        const month = d.getMonth() + 1;
        const day = d.getDate();
        const year = d.getFullYear() % 100;

        return `${month}.${day}.${year.toString().padStart(2, '0')}`;
    };


    if (hasPlaybackError) {
        return (
            <PlayerContentWrapper style={{ paddingTop: '10rem' }}>
                {getText('errorMessage')}
            </PlayerContentWrapper>
        )
    }

    return (
        <PlayerContentWrapper>
            {loading ? (
                <p>{getText('loadingMessage')}...</p>
            ) : fetchError ? (
                <p>{getText('errorMessage')}</p>
            ) : recordings.length === 0 ? (
                <p>{getText('noRecordingsMessage')}</p>
            ) : (
                <>
                    <RecordingsList>
                        {recordings.map(rec => {
                            const playback = playbackState[rec._id]

                            //prevent UI from trying to populate the default waveform before metadata is loaded,
                            //but provide necessary hidden audio element that can be updated
                            if (!playback) {
                                return <Recording key={rec._id}>
                                    <audio
                                        ref={el => {
                                            audioRefs.current[rec._id] = el;
                                        }}
                                        src={rec.audio.asset.url}
                                        preload="metadata"
                                        style={{ display: 'none' }}
                                        onPlay={() => dispatch({ type: 'PLAY', id: rec._id })}
                                        onPause={() => dispatch({ type: 'PAUSE', id: rec._id })}
                                        onEnded={() => {
                                            dispatch({ type: 'PAUSE', id: rec._id });
                                            dispatch({ type: 'RESET_PROGRESS', id: rec._id });
                                            stopProgressLoop();
                                        }}
                                        onError={handleAudioError}
                                    />

                                </Recording>
                            }

                            const progress = playback.progress
                            const isPlaying = playback.isPlaying
                            const waveformIndex = waveformIndices[rec._id]

                            return (
                                <Recording key={rec._id}>
                                    <ControlsWrapper>
                                        <AudioButton
                                            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
                                            onClick={() => togglePlay(rec._id)}
                                        >
                                            <img
                                                src={isPlaying ? '/assets/pause.svg' : '/assets/play.svg'}
                                                alt={isPlaying ? 'Pause' : 'Play'}
                                                draggable={false}
                                            />
                                        </AudioButton>
                                        <MemoizedWaveform
                                            progress={progress}
                                            waveformIndex={waveformIndex}
                                            maskId={rec._id}
                                        />
                                        <audio
                                            data-id={rec._id}
                                            src={rec.audio.asset.url}
                                            preload='metadata'
                                            style={{ display: 'none' }}
                                            ref={el => {
                                                audioRefs.current[rec._id] = el
                                            }}
                                            onPlay={() => dispatch({ type: 'PLAY', id: rec._id })}
                                            onPause={() => dispatch({ type: 'PAUSE', id: rec._id })}
                                            onEnded={() => {
                                                if (isPlaying) dispatch({ type: 'PAUSE', id: rec._id })
                                                dispatch({ type: 'RESET_PROGRESS', id: rec._id })
                                                stopProgressLoop()
                                            }}
                                            onError={handleAudioError}
                                        />
                                    </ControlsWrapper>
                                    <ContributorData>
                                        {rec.contributorName || getText('formFieldDefault')}, {rec.contributorLocation || getText('formFieldDefault')}, {formatShortDate(rec._createdAt)}
                                    </ContributorData>
                                </Recording>
                            )
                        })}
                    </RecordingsList>
                    <PaginationWrapper>
                        {prevCursors.length > 0 && (
                            <PaginationButton onClick={goPrev}>
                                {`<< ${getText('prevButtonLabel')}`}
                            </PaginationButton>
                        )}
                        {nextCursor && (
                            <PaginationButton onClick={goNext} style={{ marginLeft: 'auto', display: nextCursor ? 'inline-block' : 'none' }}>
                                {`${getText('nextButtonLabel')} >>`}
                            </PaginationButton>
                        )}
                    </PaginationWrapper>
                </>
            )}
        </PlayerContentWrapper>
    )
}
